// src/components/SEO.jsx
import React from "react";
import { Helmet } from "react-helmet-async";

export default function SEO({
  title = "Ankielabs – Creative Production, 3D Services & Courses",
  description = "Professional 3D modeling, animation, VFX, UI/UX design, CGI ads, branding and industry-standard creative courses by Ankielabs.",
  image = "https://ankielabs.com/images/anikielab.webp", // Absolute URL
  url,
  type = "website",
  schemaType = "Organization", // Dynamic schema support: Organization, Service, Course, etc.
  schemaData = {}, // Custom data to inject into JSON-LD
}) {
  // Fallback to current window URL if not provided
  const pageUrl = url || (typeof window !== "undefined" ? window.location.href : "https://ankielabs.com");

  // Base Structured Data Generator
  const getSchemaLD = () => {
    const baseOrganization = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Ankielabs",
      "alternateName": "Ankielabs Creative Production & Services",
      "url": "https://ankielabs.com",
      "logo": "https://ankielabs.com/images/anikielab.webp",
      "image": "https://ankielabs.com/images/anikielab.webp",
      "description": "Professional 3D production, VFX, animation, UI/UX design, CGI ads, and creative training courses.",
      "sameAs": [
        "https://www.youtube.com/@Ankielabs",
        "https://www.youtube.com/@Ankielabskids",
        "https://www.instagram.com/ankielabs/",
        "https://www.linkedin.com/company/ankielabs/",
        "https://www.artstation.com/ankielabs"
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "",
        "contactType": "Customer Support",
        "areaServed": "Worldwide",
        "availableLanguage": ["English"]
      }
    };

    if (schemaType === "Service") {
      return {
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": schemaData.title || title,
        "provider": {
          "@type": "Organization",
          "name": "Ankielabs",
          "url": "https://ankielabs.com"
        },
        "description": description,
        "image": image,
        "areaServed": "Worldwide",
        ...schemaData
      };
    }

    if (schemaType === "Course") {
      return {
        "@context": "https://schema.org",
        "@type": "Course",
        "name": schemaData.title || title,
        "description": description,
        "provider": {
          "@type": "Organization",
          "name": "Ankielabs",
          "sameAs": "https://ankielabs.com"
        },
        ...schemaData
      };
    }

    return { ...baseOrganization, ...schemaData };
  };

  return (
    <Helmet>
      {/* BASIC SEO & KEYWORD OPTIMIZATION */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content="3D modeling, 3D animation, VFX services, UI/UX design, CGI ads, 3D texturing, rigging, lighting, AI design, creative production agency, Ankielabs courses, learn 3D animation" />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="author" content="Ankielabs" />
      <meta name="geo.region" content="Global" />

      {/* OPEN GRAPH / FACEBOOK / LINKEDIN */}
      <meta property="og:site_name" content="Ankielabs" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:secure_url" content={image} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:locale" content="en_US" />

      {/* TWITTER CARD */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@Ankielabs" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* FAVICON */}
      <link rel="icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" href="/images/anikielab.webp" />

      {/* CANONICAL URL (Prevents duplicate content issues) */}
      <link rel="canonical" href={pageUrl} />

      {/* JSON-LD Structured Data for Google Rich Results */}
      <script type="application/ld+json">
        {JSON.stringify(getSchemaLD())}
      </script>
    </Helmet>
  );
}